import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useState } from "react";
import { useTranslation } from "react-i18next";

export type EarningsItem = {
  id: string;
  name: string;
  avatarUrl: string;
  date: string;
  amount: number;
  status: "paid" | "pending";
  role: string;
  location?: string;
};

export function EarningsSidebar({
  selectedId,
  onSelect,
  summary,
  mode,
}: {
  selectedId: string | null;
  onSelect: (id: string) => void;
  summary: Array<any> | undefined;
  mode: string;
}) {
  const { t } = useTranslation("payments");
  const [tab, setTab] = useState<string>("all");

  // Map API data to UI
  const earnings: Array<EarningsItem> = (summary || []).map((p: any) => ({
    id: String(p.payment_id),
    name: mode === "worker" ? p.business_name : p.worker_name,
    avatarUrl: mode === "worker" ? p.business_image : p.worker_avatar,
    date: new Date(p.created_at).toLocaleDateString(undefined, {
      month: "short",
      day: "numeric",
    }),
    amount: p.payable,
    status: p.payment_status === "completed" ? "paid" : "pending",
    role: p.role,
    location: p.location || "",
  }));

  const filtered =
    tab === "all"
      ? earnings
      : tab === "paid"
        ? earnings.filter((e) => e.status === "paid")
        : earnings.filter((e) => e.status === "pending");

  return (
    <aside className="bg-white rounded-2xl shadow p-4 flex flex-col min-w-[320px] max-w-[320px]">
      <Tabs value={tab} onValueChange={setTab} className="flex-1">
        <TabsList
          className="w-full pt-4 pb-0 bg-transparent"
          style={{ borderColor: "rgba(84, 90, 99, 0.25)" }}
        >
          <TabsTrigger value="all" className={"body-small text-slate!"}>
            {t("tabs.all")}
          </TabsTrigger>
          <TabsTrigger value="paid" className={"body-small text-slate!"}>
            {t("tabs.paid")}
          </TabsTrigger>
          <TabsTrigger value="pending" className={"body-small text-slate!"}>
            {t("tabs.pending")}
          </TabsTrigger>
        </TabsList>
        <div className="mb-2 text-xs text-garnet/70 font-semibold">
          {new Date().toLocaleDateString(undefined, {
            weekday: "long",
            month: "short",
            day: "numeric",
            year: "numeric",
          })}
        </div>
        <TabsContent className="p-0" value="all">
          <ul>
            {filtered.map((item) => (
              <li key={item.id}>
                <button
                  className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg transition-colors ${selectedId === item.id ? "bg-gold/40 border-2 border-gold" : "hover:bg-accent/20"}`}
                  onClick={() => onSelect(item.id)}
                >
                  <img
                    src={item.avatarUrl}
                    alt="avatar"
                    className="w-8 h-8 rounded-full bg-gray-100"
                  />
                  <div className="flex-1 text-left">
                    <div className="font-semibold text-garnet text-sm">
                      {item.name}
                    </div>
                    <div className="text-xs text-garnet/70">{item.date}</div>
                  </div>
                  <div className="font-bold text-garnet text-base">
                    ${item.amount.toFixed(2)}
                  </div>
                </button>
              </li>
            ))}
          </ul>
        </TabsContent>
        <TabsContent className="p-0" value="paid">
          <ul>
            {filtered.map((item) => (
              <li key={item.id}>
                <button
                  className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg transition-colors ${selectedId === item.id ? "bg-gold/40 border-2 border-gold" : "hover:bg-accent/20"}`}
                  onClick={() => onSelect(item.id)}
                >
                  <img
                    src={item.avatarUrl}
                    alt="avatar"
                    className="w-8 h-8 rounded-full bg-gray-100"
                  />
                  <div className="flex-1 text-left">
                    <div className="font-semibold text-garnet text-sm">
                      {item.name}
                    </div>
                    <div className="text-xs text-garnet/70">{item.date}</div>
                  </div>
                  <div className="font-bold text-garnet text-base">
                    ${item.amount.toFixed(2)}
                  </div>
                </button>
              </li>
            ))}
          </ul>
        </TabsContent>
        <TabsContent className="p-0" value="pending">
          <ul>
            {filtered.map((item) => (
              <li key={item.id}>
                <button
                  className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg transition-colors ${selectedId === item.id ? "bg-gold/40 border-2 border-gold" : "hover:bg-accent/20"}`}
                  onClick={() => onSelect(item.id)}
                >
                  <img
                    src={item.avatarUrl}
                    alt="avatar"
                    className="w-8 h-8 rounded-full bg-gray-100"
                  />
                  <div className="flex-1 text-left">
                    <div className="font-semibold text-garnet text-sm">
                      {item.name}
                    </div>
                    <div className="text-xs text-garnet/70">{item.date}</div>
                  </div>
                  <div className="font-bold text-garnet text-base">
                    ${item.amount.toFixed(2)}
                  </div>
                </button>
              </li>
            ))}
          </ul>
        </TabsContent>
      </Tabs>
    </aside>
  );
}
